import type {
  AccountView,
  AnswerBody,
  AssertionOptions,
  AuditVerdict,
  BillerView,
  BillView,
  BundleView,
  CardView,
  Channel,
  CredentialView,
  DraftOutcome,
  Evidence,
  Health,
  HistoryRange,
  HistoryRead,
  HoldingView,
  InstrumentRef,
  KycDeclared,
  KycMyinfoAnswer,
  KycRefreshStarted,
  LedgerEntry,
  LimitsView,
  MandateCancel,
  MandateDetail,
  MandateView,
  PayeeView,
  PendingView,
  PortfolioOrders,
  Preferences,
  ProxyLookup,
  Receipt,
  RunDue,
  SessionView,
  StatementLine,
  SupportContact,
  Valuation,
} from "./types.js";
import type { ScamAnswer } from "../app/scam.js";
import type { SpendingReport } from "../app/reads.js";
import type { SpendPeriod, SpendRange } from "../adapters/reads.js";
import { bankTimeOf } from "../lib/bank-clock.js";

/**
 * The one place this app talks to the gateway.
 *
 * Same origin, cookie session, CSRF double-submit (`x-dcta-csrf`) on every
 * unsafe method — the backend's rule, not ours (lab/dcta README, DCTA-62).
 * Every failure becomes an `ApiError` carrying the server's reason code, so no
 * screen ever has to invent one and no screen ever shows "UNKNOWN".
 */
export class ApiError extends Error {
  readonly reasonCode: string;
  readonly status: number;
  readonly body: Record<string, unknown>;
  /** DCTA-185: how long the bank asked to wait (its Retry-After), in whole seconds — only when it said. */
  readonly retryAfterSeconds: number | undefined;

  constructor(reasonCode: string, status: number, body: Record<string, unknown>, retryAfterSeconds?: number) {
    super(reasonCode);
    this.name = "ApiError";
    this.reasonCode = reasonCode;
    this.status = status;
    this.body = body;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** The deterministic sentence the register carries, when there is one. */
  get serverMessage(): string | undefined {
    const message = this.body["message"];
    return typeof message === "string" ? message : undefined;
  }
}

export const OFFLINE = "OFFLINE";

/** RFC 9110's preferred HTTP date (IMF-fixdate), e.g. "Mon, 12 Oct 2026 02:01:30 GMT". */
const HTTP_DATE = /^[A-Z][a-z]{2}, \d{2} [A-Z][a-z]{2} \d{4} \d{2}:\d{2}:\d{2} GMT$/;

/**
 * DCTA-185: a Retry-After header as whole seconds — delta-seconds as sent, or an HTTP date counted down from now (at
 * least one second); anything else (no header, junk, a negative or a fraction) is none.
 */
export function retryAfterSeconds(header: string | null, nowMs = Date.now()): number | undefined {
  const value = header?.trim() ?? "";
  if (/^\d+$/.test(value)) return Number(value);
  if (!HTTP_DATE.test(value)) return undefined;
  const at = Date.parse(value);
  return Number.isNaN(at) ? undefined : Math.max(1, Math.ceil((at - nowMs) / 1000));
}

export class Api {
  #csrf = "";
  /** Told the bank's time whenever an answer carries it (DCTA-85 P1-2, `bankTimeOf`). */
  readonly #onBankTime: ((iso: string) => void) | undefined;
  /** QA-04: asked when a request fails — is the page going away (lib/page-leaving.ts)? Absent: never. */
  readonly #leaving: (() => Promise<boolean>) | undefined;
  /** DCTA-221 review: the page came back (the back-forward cache's `pageshow`) — a request parked while it went settles. */
  readonly #back: (() => Promise<void>) | undefined;

  constructor(onBankTime?: (iso: string) => void, opts: { readonly leaving?: () => Promise<boolean>; readonly back?: () => Promise<void> } = {}) {
    this.#onBankTime = onBankTime;
    this.#leaving = opts.leaving;
    this.#back = opts.back;
  }

  /**
   * The request never reached an answer. QA-04: one the page's own reload cancelled is no failure to report — it was sent,
   * and the page is going away — so it does not settle while the page goes: nothing is drawn over it, and the turn it carried
   * stays in flight for the page's next life. DCTA-221 review: if the page comes BACK instead (the back-forward cache), the
   * answer is lost to this page — OFFLINE then, never a request held for good (a turn spinning to its give-up, a signature
   * holding Back). Anything else is OFFLINE, as always.
   */
  async #unanswered(): Promise<never> {
    if (this.#leaving !== undefined && (await this.#leaving())) {
      if (this.#back === undefined) return new Promise<never>(() => undefined);
      await this.#back();
    }
    throw new ApiError(OFFLINE, 0, {});
  }

  get csrf(): string {
    return this.#csrf;
  }

  /**
   * QA-04: after a reload the session's cookie is still the browser's, its double-submit token is not — the token kept for
   * this tab (app/resume.ts) is given back. Empty or absent changes nothing.
   */
  restoreCsrf(token: string | undefined): void {
    if (typeof token === "string" && token !== "") this.#csrf = token;
  }

  async #call<T>(method: "GET" | "POST" | "PATCH", path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (this.#csrf !== "") headers["x-dcta-csrf"] = this.#csrf;
    let response: Response;
    try {
      response = await fetch(path, {
        method,
        headers,
        credentials: "same-origin",
        cache: "no-store",
        // A POST that declares JSON and sends zero bytes is malformed JSON, and
        // this service answers 400 rather than guessing (lab console, rule 1).
        ...(method === "GET" ? {} : { body: JSON.stringify(body ?? {}) }),
      });
    } catch {
      return await this.#unanswered();
    }
    let json: Record<string, unknown> = {};
    const wait = response.ok ? undefined : retryAfterSeconds(response.headers.get("retry-after"));
    try {
      json = (await response.json()) as Record<string, unknown>;
    } catch {
      if (!response.ok) throw new ApiError("INTERNAL_ERROR", response.status, {}, wait);
    }
    if (!response.ok) {
      const code = typeof json["reason_code"] === "string" ? json["reason_code"] : "INTERNAL_ERROR";
      throw new ApiError(code, response.status, json, wait);
    }
    const said = bankTimeOf(path.split("?")[0] ?? path, json);
    if (said !== undefined) this.#onBankTime?.(said);
    // The register uses HTTP 200 for outcomes that are refusals in the domain
    // (a refused draft is a valid answer, not a transport failure) — those carry
    // a `reason_code` in a 200 body and are handled by the caller, not here.
    return json as T;
  }

  // ---------------------------------------------------------------- session
  async login(customerId: string, profile?: string): Promise<SessionView> {
    const session = await this.#call<SessionView>("POST", "/v1/session/login", {
      customer_id: customerId,
      ...(profile === undefined ? {} : { accessibility_profile: profile }),
    });
    if (typeof session.csrf_token === "string") this.#csrf = session.csrf_token;
    return session;
  }

  /**
   * P0-1 (DCTA-192): the customers the door may offer, as the bank lists them (`MOCK_LOGIN=1` only — anywhere else the same
   * 404 as an unknown path). The door reads the ids alone (app/door-model.ts › offered); nothing here becomes a row.
   */
  personas(): Promise<{ readonly personas: readonly { readonly customer_id: string }[] }> {
    return this.#call("GET", "/v1/session/personas");
  }

  session(): Promise<SessionView> {
    return this.#call<SessionView>("GET", "/v1/session");
  }

  logout(): Promise<unknown> {
    return this.#call("POST", "/v1/session/logout");
  }

  /** Me's switches (DCTA-74 ask k): the bank keeps them, and picks the signing window from large type. */
  preferences(patch: { readonly large_type?: boolean; readonly notifications?: boolean; readonly read_aloud?: Preferences["read_aloud"] }): Promise<{ preferences: Preferences }> {
    return this.#call("PATCH", "/v1/customer/preferences", patch);
  }

  credentials(): Promise<{ credentials: readonly CredentialView[] }> {
    return this.#call("GET", "/v1/webauthn/credentials");
  }

  registrationOptions(): Promise<Record<string, never> & PublicKeyCredentialCreationOptionsJSON> {
    return this.#call("POST", "/v1/webauthn/registration/options");
  }

  registrationVerify(response: unknown): Promise<unknown> {
    return this.#call("POST", "/v1/webauthn/registration/verify", { response });
  }

  // ----------------------------------------------------------------- drafts
  /**
   * A refusal is a valid answer, not a transport failure, so the service
   * returns several of them as HTTP 200 with a `reason_code` and no `kind`
   * (a frozen draft, a blocked instruction). Those become `ApiError` here so
   * that every refusal reaches the UI through one path. A 200 that DOES carry a
   * `kind` is an outcome envelope and is returned as one.
   */
  async draft(input: { transcript: string; draft: unknown; drafter?: unknown; channel: Channel }): Promise<DraftOutcome> {
    return Api.#outcome(await this.#withChannel<DraftOutcome & { reason_code?: string }>("/v1/drafts", input));
  }

  /**
   * The model path (DCTA-97, DCTA-108): the customer's words and how they came — the bank drafts,
   * validates and decides; nothing else travels (the route's body is strict). A bank that runs no
   * model answers 404 NOT_FOUND (the route does not exist there); `app/drafting.ts` reads that as
   * "this bank drafts from the reviewed fixtures", once a session.
   */
  async utterance(input: { transcript: string; channel: Channel; source?: "reask"; audio_id?: string }): Promise<DraftOutcome> {
    return Api.#outcome(await this.#withChannel<DraftOutcome & { reason_code?: string }>("/v1/utterances", input));
  }

  /**
   * DCTA-191 (docs/specs/fe-audio-evidence-dcta191.md, ADR-0024): the listening's clip, sent BEFORE its words — the WAV's
   * bytes as the body (`audio/wav`, no-store, the CSRF header). `201` names it for the words (`audio_id`); any other
   * answer is an `ApiError`, and the words go without it.
   */
  async uploadUtteranceAudio(wav: Uint8Array<ArrayBuffer>): Promise<{ audio_id: string; sha256: string; bytes: number; duration_ms: number; retained_until: string }> {
    const response = await this.#raw("POST", "/v1/utterances/audio", wav);
    const json = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (!response.ok || typeof json["audio_id"] !== "string") {
      throw new ApiError(typeof json["reason_code"] === "string" ? json["reason_code"] : "INTERNAL_ERROR", response.status, json);
    }
    return json as unknown as { audio_id: string; sha256: string; bytes: number; duration_ms: number; retained_until: string };
  }

  /** DCTA-191: a kept clip's bytes as stored (judge mode's Proof plays and re-hashes them); `undefined` once deleted (404). */
  async utteranceAudio(audioId: string): Promise<Uint8Array | undefined> {
    const response = await this.#raw("GET", `/v1/utterances/audio/${encodeURIComponent(audioId)}`);
    if (response.status === 404) return undefined;
    if (!response.ok) {
      const json = (await response.json().catch(() => ({}))) as Record<string, unknown>;
      throw new ApiError(typeof json["reason_code"] === "string" ? json["reason_code"] : "INTERNAL_ERROR", response.status, json);
    }
    return new Uint8Array(await response.arrayBuffer());
  }

  /** A request whose body or answer is bytes, not JSON (DCTA-191's audio): same origin, the CSRF header, no-store. */
  async #raw(method: "GET" | "POST", path: string, body?: Uint8Array<ArrayBuffer>): Promise<Response> {
    const headers: Record<string, string> = body === undefined ? {} : { "content-type": "audio/wav" };
    if (this.#csrf !== "") headers["x-dcta-csrf"] = this.#csrf;
    try {
      return await fetch(path, { method, headers, credentials: "same-origin", cache: "no-store", ...(body === undefined ? {} : { body }) });
    } catch {
      return await this.#unanswered();
    }
  }

  /**
   * DCTA-188 (judge mode, beat 5): the faulty-drafter demo — the words, the one fault, how they came. The bank answers as
   * the utterance route does (a freeze is a 200 with a `reason_code`, so an `ApiError` here, its `demo_fault` in the body),
   * or with `kind: "demo_result"` when nothing was caught; never with a card. 404 where the bank runs no model path.
   */
  async faultyDrafter(input: { transcript: string; channel: Channel }): Promise<DraftOutcome> {
    return Api.#outcome(await this.#call<DraftOutcome & { reason_code?: string }>("POST", "/v1/demo/faulty-drafter", { transcript: input.transcript, fault: "swap_amounts", channel: input.channel }));
  }

  /** DCTA-188: judge mode's demo routes answer here (DEMO_MODE=1); a 404 means this bank has none. */
  demoState(): Promise<Record<string, unknown>> {
    return this.#call("GET", "/v1/demo/state");
  }

  /** Routes that refused the `channel` field once this session (a bank older than DCTA-109). */
  readonly #noChannel = new Set<string>();

  /**
   * DCTA-111: a front end and a bank are deployed one after the other, so a build that sends
   * `channel` can meet a bank whose strict body schema does not know it yet (400 INVALID_REQUEST,
   * which names no field). One retry without the field; if THAT is answered, this route is never
   * sent the field again this session. If the retry is refused too, the 400 was about something
   * else: the bank's answer stands and nothing is remembered. A 400 is refused at validation,
   * before any handler runs — nothing was drafted by the first attempt.
   */
  async #withChannel<T>(path: string, body: { readonly channel: Channel } & Record<string, unknown>): Promise<T> {
    const { channel: _said, ...bare } = body;
    if (this.#noChannel.has(path)) return this.#call<T>("POST", path, bare);
    try {
      return await this.#call<T>("POST", path, body);
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 400 && error.reasonCode === "INVALID_REQUEST")) throw error;
      const answer = await this.#call<T>("POST", path, bare);
      this.#noChannel.add(path);
      return answer;
    }
  }

  /**
   * 04: the customer's answer to ONE question, by id — a choice the server
   * offered, a typed figure where the question takes an amount, or the customer's
   * own words where it takes words (R-U1, DCTA-123 (nn): the bank's firewall reads
   * them first). The server pins it and re-runs the same pipeline, so the answer
   * comes back as a new outcome (another question, a card, a refusal) exactly like
   * a sentence does.
   */
  async answer(draftId: string, body: AnswerBody): Promise<DraftOutcome> {
    return Api.#outcome(
      await this.#call<DraftOutcome & { reason_code?: string }>(
        "POST",
        `/v1/drafts/${encodeURIComponent(draftId)}/answer`,
        body,
      ),
    );
  }

  static #outcome(body: DraftOutcome & { reason_code?: string }): DraftOutcome {
    if (typeof body.reason_code === "string" && body.kind === undefined) {
      throw new ApiError(body.reason_code, 200, body as unknown as Record<string, unknown>);
    }
    return body;
  }

  bundle(bundleId: string): Promise<BundleView> {
    return this.#call<BundleView>("GET", `/v1/bundles/${encodeURIComponent(bundleId)}`);
  }

  /**
   * DCTA-170 (backend B46, docs/design/v3/03-edit-in-place.md §0.1; not built yet): one edit of a card to sign, applied by
   * the bank (a new bundle) or previewed (what would change, stored nowhere). `adapters/amend.ts` reads the answer.
   */
  amend(bundleId: string, body: { readonly base_sha256: string; readonly mode: "apply" | "preview"; readonly edits: readonly unknown[] }): Promise<Record<string, unknown>> {
    return this.#call("POST", `/v1/bundles/${encodeURIComponent(bundleId)}/amend`, body);
  }

  assertionOptions(bundleId: string): Promise<AssertionOptions> {
    return this.#call<AssertionOptions>(
      "POST",
      `/v1/bundles/${encodeURIComponent(bundleId)}/assertion-options`,
    );
  }

  // ---------------------------------------------------------------- gateway
  /**
   * The signature, and — for a step the policy asked two questions about — the
   * customer's two answers by id (DCTA-74 ask f). The gateway checks the answers
   * against the STORED interventions before it verifies the signature.
   */
  confirm(bundleId: string, assertion: unknown, answers: readonly ScamAnswer[] = []): Promise<Receipt> {
    return this.#call<Receipt>(
      "POST",
      `/v1/gateway/bundles/${encodeURIComponent(bundleId)}/confirm`,
      answers.length === 0 ? { assertion } : { assertion, answers },
    );
  }

  stepUp(bundleId: string, code: string): Promise<Receipt> {
    return this.#call<Receipt>("POST", `/v1/gateway/bundles/${encodeURIComponent(bundleId)}/step-up`, {
      code,
    });
  }

  /**
   * 05, DCTA-74 ask r: the waiting second factor moves to the bank's call (or back to
   * the token). Same factor, same window, same bytes — nothing here authorises money.
   */
  stepUpMethod(bundleId: string, method: "rm_callback" | "hardware_token"): Promise<{ state: string; step_up: NonNullable<Receipt["step_up"]> }> {
    return this.#call("POST", `/v1/gateway/bundles/${encodeURIComponent(bundleId)}/step-up/method`, { method });
  }

  /**
   * DCTA-154 F27 (a) (backend DCTA-157 B38): a Yes at the scam check is a cancel that carries the card's answers, so the
   * bank learns the Yes and carries it for that payee; a plain cancel (a change of mind) carries none.
   */
  cancelBundle(bundleId: string, reason: string, answers: readonly ScamAnswer[] = []): Promise<{ state: string }> {
    return this.#call("POST", `/v1/gateway/bundles/${encodeURIComponent(bundleId)}/cancel`, answers.length === 0 ? { reason } : { reason, answers });
  }

  /** DCTA-154: the Limits page's one read — the limits the bank applies to this customer, today's counters, the waits. */
  limits(): Promise<LimitsView> {
    return this.#call<LimitsView>("GET", "/v1/limits");
  }

  receipt(bundleId: string): Promise<Receipt> {
    return this.#call<Receipt>("GET", `/v1/gateway/bundles/${encodeURIComponent(bundleId)}`);
  }

  /** Tightening only. The fast path: no draft, no model, no signature (UC-09). */
  lockFunds(): Promise<{ result_summary: string; voided_bundles: readonly string[] }> {
    return this.#call("POST", "/v1/protection/actions", { action: "lock_funds" });
  }

  /**
   * 15 Cards (DCTA-111): freeze the cards linked to one account — the fast path's only card target
   * (a card-level target is a backend ask). Tightening only; no signature.
   */
  freezeCards(accountId: string): Promise<{ result_summary: string }> {
    return this.#call("POST", "/v1/protection/actions", { action: "freeze_card", target_account_id: accountId });
  }

  // ------------------------------------------------- scheduled payments (DCTA-209, spec §9)
  /**
   * Route 1: the customer's schedules — active first by next date, then ended newest first. `status: "active"` narrows to
   * the live ones; `destination_id` (B76) to one payee, biller or own account. A read: nothing here changes a schedule.
   */
  mandates(o: { readonly status?: "active"; readonly destination_id?: string } = {}): Promise<{ mandates: readonly MandateView[] }> {
    const q = new URLSearchParams();
    if (o.destination_id !== undefined) q.set("destination_id", o.destination_id);
    if (o.status !== undefined) q.set("status", o.status);
    const query = q.toString();
    return this.#call("GET", `/v1/mandates${query === "" ? "" : `?${query}`}`);
  }

  /** Route 2: one schedule and every payment it decided (404 for an id that is not this customer's). */
  mandate(mandateId: string): Promise<MandateDetail> {
    return this.#call("GET", `/v1/mandates/${encodeURIComponent(mandateId)}`);
  }

  /**
   * Route 3 (spec §9.2): Stop — protective, so no passkey and no token; the session's CSRF header, body `{}`. An earlier stop
   * answers `changed: false` with 200: never an error.
   */
  cancelMandate(mandateId: string): Promise<MandateCancel> {
    return this.#call("POST", `/v1/mandates/${encodeURIComponent(mandateId)}/cancel`, {});
  }

  /**
   * Route 4 (spec §9.4), judge mode only (DEMO_MODE=1, an ordinary 404 anywhere else): the caller's demo world's clock moves
   * to the earliest next due time and every payment due then runs through the bank's checks.
   */
  runDue(): Promise<RunDue> {
    return this.#call("POST", "/v1/demo/scheduler/run-due", {});
  }

  // ------------------------------------------------------------- bank reads
  accounts(): Promise<{ accounts: readonly AccountView[]; as_of?: string }> {
    return this.#call("GET", "/v1/accounts");
  }

  /** The customer's own biller registry (judge mode's E5 reads whose world it runs in — QA-014; DCTA-203's bills read). */
  billers(): Promise<{ billers: readonly BillerView[] }> {
    return this.#call("GET", "/v1/billers");
  }

  /** DCTA-203: the outstanding bills — what is still owed on each, from the book. */
  bills(): Promise<{ bills: readonly BillView[] }> {
    return this.#call("GET", "/v1/bills");
  }

  /** DCTA-203: what the customer may still be waiting on — signed or held bundles. */
  pending(): Promise<{ pending: readonly PendingView[] }> {
    return this.#call("GET", "/v1/gateway/pending");
  }

  /** The customer's profile; DCTA-208 reads which account is the default debit account (its card leads "Which card?"). */
  customer(): Promise<{ readonly default_debit_account?: string }> {
    return this.#call("GET", "/v1/customers/me");
  }

  cards(): Promise<{ cards: readonly CardView[] }> {
    return this.#call("GET", "/v1/cards");
  }

  /** The holdings and (DCTA-229, §4.1) the trading account they settle from — its available balance is the cash to invest. */
  portfolio(): Promise<{ holdings: readonly HoldingView[]; settlement_account?: AccountView }> {
    return this.#call("GET", "/v1/portfolio");
  }

  // ------------------------------------------------ DCTA-229: the Invest tab's optional reads (spec 10-invest §4.2, §4.3)
  /** A: what the shares were worth over the range, day by day — undefined when the bank has no such read (the fallback). */
  portfolioHistory(range: HistoryRange): Promise<HistoryRead | undefined> {
    return this.#optional(`/v1/portfolio/history?range=${encodeURIComponent(range)}`);
  }

  /** B: one instrument's demo closes over the range, in its own currency — undefined when the bank has none. */
  instrumentHistory(instrumentId: string, range: HistoryRange): Promise<HistoryRead | undefined> {
    return this.#optional(`/v1/instruments/${encodeURIComponent(instrumentId)}/history?range=${encodeURIComponent(range)}`);
  }

  /** C: the newest share orders with their settlement — undefined when the bank has no such read (Orders from the ledger). */
  portfolioOrders(limit = 3): Promise<PortfolioOrders | undefined> {
    return this.#optional(`/v1/portfolio/orders?limit=${String(limit)}`);
  }

  /** A read the bank may not have: its 404 is `undefined` (§4.3's rung); every other failure is thrown as always. */
  async #optional<T>(path: string): Promise<T | undefined> {
    try {
      return await this.#call<T>("GET", path);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return undefined;
      throw error;
    }
  }

  /** What the portfolio is worth, in the bank's figures and base currency (ask n; P2-11). */
  valuation(): Promise<Valuation> {
    return this.#call("GET", "/v1/portfolio/valuation");
  }

  quote(instrumentId: string): Promise<{ instrument_id: string; quote: { price: string; currency: string; as_of: string } }> {
    return this.#call("GET", `/v1/instruments/${encodeURIComponent(instrumentId)}/quote`);
  }

  /** The newest ledger entries across the customer's accounts (Home's Recent); DCTA-204 (R1): one account's, when named. */
  ledgerEntries(limit = 3, accountId?: string): Promise<{ entries: readonly LedgerEntry[] }> {
    return this.#call("GET", `/v1/ledger/entries?limit=${String(limit)}${accountId === undefined ? "" : `&account_id=${encodeURIComponent(accountId)}`}`);
  }

  /**
   * Spend by category for a period (DCTA-74 ask b): the bank's sum, so no answer adds anything up. DCTA-203: any of the
   * read's periods; DCTA-206 (A1): or the bank's own dates (`from`/`to`, never beside `period` — the route refuses both).
   */
  spending(when: SpendPeriod | SpendRange = "this_month"): Promise<SpendingReport> {
    const what = typeof when === "string" ? `period=${when}` : `from=${encodeURIComponent(when.from)}&to=${encodeURIComponent(when.to)}`;
    return this.#call("GET", `/v1/spending?${what}&compare_to=same_dates_last_month`);
  }

  /** A card's full details for the bank's own window (ask a): session-only, POST, never cached. */
  cardDetails(cardId: string): Promise<{ pan: string; expiry: string; cvv: string; visible_seconds: number; visible_until: string }> {
    return this.#call("POST", `/v1/cards/${encodeURIComponent(cardId)}/details`);
  }

  /** What "Talk to us" dials — the operator's number and hours (DCTA-74 run 3). */
  support(): Promise<SupportContact> {
    return this.#call("GET", "/v1/support");
  }

  health(): Promise<Health> {
    return this.#call("GET", "/healthz");
  }

  /** The registry's own name for an instrument, so Holdings need not print ids. */
  instruments(query: string): Promise<{ instruments: readonly InstrumentRef[] }> {
    return this.#call("GET", `/v1/instruments?q=${encodeURIComponent(query)}`);
  }

  statement(accountId: string, limit = 6): Promise<{ entries: readonly StatementLine[] }> {
    return this.#call(
      "GET",
      `/v1/accounts/${encodeURIComponent(accountId)}/transactions?limit=${String(limit)}`,
    );
  }

  /** The whole chain's verdict (judge mode's Chain panel). */
  verifyChain(): Promise<AuditVerdict> {
    return this.#call<AuditVerdict>("GET", "/v1/audit/verify");
  }

  /** Said → parsed → shown → signed → executed, for one of the customer's bundles (judge mode's proof). */
  evidence(bundleId: string): Promise<Evidence> {
    return this.#call<Evidence>("GET", `/v1/audit/bundles/${encodeURIComponent(bundleId)}/evidence`);
  }

  /** DEMO_MODE only: edit one audit record on purpose, so the chain's check can be seen to break. */
  tamper(auditRef: string, field: string, value: string): Promise<{ tampered: string }> {
    return this.#call("POST", "/v1/demo/audit/tamper", { audit_ref: auditRef, field, value });
  }

  /** DEMO_MODE only: move the bank's clock forward (never back), in seconds from real time. */
  demoClock(offsetSeconds: number): Promise<{ clock_offset_seconds: number; now: string }> {
    return this.#call("POST", "/v1/demo/clock", { offset_seconds: offsetSeconds });
  }

  /** The customer's saved payees (17 Payees; the E5 attack checks nobody added one). */
  payees(): Promise<{ payees: readonly PayeeView[] }> {
    return this.#call("GET", "/v1/payees");
  }

  /** One payee, read fresh for its page (17). */
  payee(payeeId: string): Promise<PayeeView> {
    return this.#call("GET", `/v1/payees/${encodeURIComponent(payeeId)}`);
  }

  /**
   * 17 at `add` (DCTA-120): the PayNow directory's registered name for one proxy. A POST, so the number
   * never enters a URL or a log (the bank's own rule); nothing is saved by it.
   */
  paynowLookup(proxyType: string, proxy: string): Promise<ProxyLookup> {
    return this.#call("POST", "/v1/paynow/lookup", { proxy_type: proxyType, proxy });
  }

  /**
   * DCTA-124 (§F.2 row 13; DCTA-123 ASK-13): 17's Check and sign — the bank builds a card that adds a
   * payee from the screen's request and nothing else (the number, and the customer's own name for
   * them). The answer has the shape of a draft's signing outcome; the card is drawn from
   * `GET /v1/bundles/{id}`, and signed and executed by the gateway like any other.
   */
  addPayee(body: { readonly proxy_type: string; readonly proxy: string; readonly nickname?: string }): Promise<{ readonly kind: string; readonly bundle_id: string }> {
    return this.#call("POST", "/v1/payees/additions", body);
  }

  verifyAudit(bundleId: string): Promise<AuditVerdict> {
    return this.#call<AuditVerdict>(
      "GET",
      `/v1/audit/verify?bundle_id=${encodeURIComponent(bundleId)}`,
    );
  }

  /**
   * DCTA-176 (B60, F49): the caller's OWN audit records, a page — position and identity only (`seq`, `type`, `audit_ref`,
   * `recorded_at`, `bundle_id`), never a body. Judge mode's Proof finds the refused turn's and the refresh's records here.
   */
  auditRecords(fromSeq = 1, limit = 200): Promise<{ readonly records: readonly AuditRecordRef[]; readonly next_from_seq?: string }> {
    return this.#call("GET", `/v1/audit/records?from_seq=${String(fromSeq)}&limit=${String(limit)}`);
  }

  // ------------------------------------------------- the identity check (DCTA-176, B58)
  /** Start a refresh: what the demo Myinfo would share, when the bank last checked, the window (409 when up to date). */
  kycRefreshStart(): Promise<KycRefreshStarted> {
    return this.#call("POST", "/v1/kyc/refresh", {});
  }

  /** The customer's answer to the Myinfo consent: the details, or nothing shared (503 MYINFO_UNAVAILABLE). */
  kycMyinfo(refreshId: string, consent: "given" | "declined"): Promise<KycMyinfoAnswer> {
    return this.#call("POST", `/v1/kyc/refresh/${encodeURIComponent(refreshId)}/myinfo`, { consent });
  }

  /** The customer's own details → the bank builds the card (one `kyc_declaration` step), signed through the gateway. */
  kycDeclaration(refreshId: string, body: { readonly occupation: string; readonly source_of_funds: readonly string[] }): Promise<KycDeclared> {
    return this.#call("POST", `/v1/kyc/refresh/${encodeURIComponent(refreshId)}/declaration`, body);
  }
}

/** One of the caller's own audit records, by position (DCTA-77 ask aa). */
export interface AuditRecordRef {
  readonly seq: string;
  readonly type: string;
  readonly audit_ref: string;
  readonly recorded_at: string;
  readonly bundle_id?: string;
}


/** Minimal shape of what the enrolment route returns; the DOM types are stricter. */
export interface PublicKeyCredentialCreationOptionsJSON {
  readonly challenge: string;
  readonly rp: { readonly id: string; readonly name: string };
  readonly user: { readonly id: string; readonly name: string; readonly displayName: string };
  readonly pubKeyCredParams: readonly PublicKeyCredentialParameters[];
  readonly timeout?: number;
  readonly attestation?: AttestationConveyancePreference;
  readonly authenticatorSelection?: AuthenticatorSelectionCriteria;
  readonly excludeCredentials?: readonly { readonly id: string; readonly transports?: string[] }[];
}
